import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Helper to add auth token to image URLs for protected media
const getAuthenticatedImageUrl = (url) => {
  if (!url) return url;
  const token = api.getAccessToken();
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${token}`;
};

/**
 * Photo picker modal for selecting photos to include in PDFs
 * @param {Object} props
 * @param {Array} props.photos - Array of photo objects with id, image_url, caption, created_at, captured_at, latitude, longitude
 * @param {Array} props.selectedIds - Currently selected photo IDs
 * @param {Function} props.onConfirm - Called with array of selected photo IDs when confirmed
 * @param {Function} props.onClose - Called when modal is closed
 * @param {string} props.title - Modal title (default: "Select Photos")
 */
function PhotoPickerModal({ photos, selectedIds = [], onConfirm, onClose, title = 'Select Photos' }) {
  const [selected, setSelected] = useState(new Set(selectedIds));

  useEffect(() => {
    setSelected(new Set(selectedIds));
  }, [selectedIds]);

  const togglePhoto = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const selectAll = () => {
    setSelected(new Set(photos.map(p => p.id)));
  };

  const selectNone = () => {
    setSelected(new Set());
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatLocation = (lat, lng) => {
    if (!lat || !lng) return null;
    return `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h3 className="text-xl font-bold text-primary">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {selected.size} of {photos.length} photos selected
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Selection Controls */}
        <div className="px-6 py-3 border-b border-slate-100 flex gap-2">
          <button
            onClick={selectAll}
            className="px-3 py-1.5 text-sm font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition"
          >
            <i className="fas fa-check-double mr-1"></i>
            Select All
          </button>
          <button
            onClick={selectNone}
            className="px-3 py-1.5 text-sm font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition"
          >
            <i className="fas fa-times mr-1"></i>
            Select None
          </button>
        </div>

        {/* Photo Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {photos.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <i className="fas fa-camera text-4xl text-slate-300 mb-3"></i>
              <p>No photos available</p>
              <p className="text-sm">Photos can be added from the mobile app</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map((photo) => {
                const isSelected = selected.has(photo.id);
                const location = formatLocation(photo.latitude, photo.longitude);
                const photoDate = formatDate(photo.captured_at || photo.created_at);

                return (
                  <div
                    key={photo.id}
                    onClick={() => togglePhoto(photo.id)}
                    className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition ${
                      isSelected
                        ? 'border-secondary shadow-lg ring-2 ring-secondary/20'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Photo */}
                    <div className="aspect-square bg-slate-100">
                      <img
                        src={getAuthenticatedImageUrl(photo.image_url || photo.image)}
                        alt={photo.caption || 'Job photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Selection Indicator */}
                    <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected
                        ? 'bg-secondary text-white'
                        : 'bg-white/80 border border-slate-300'
                    }`}>
                      {isSelected && <i className="fas fa-check text-xs"></i>}
                    </div>

                    {/* Photo Info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      {photo.caption && (
                        <p className="text-white text-xs truncate">{photo.caption}</p>
                      )}
                      <div className="flex items-center gap-2 text-white/80 text-xs">
                        {photoDate && (
                          <span>
                            <i className="fas fa-calendar-alt mr-1"></i>
                            {photoDate}
                          </span>
                        )}
                        {location && (
                          <span>
                            <i className="fas fa-map-marker-alt mr-1"></i>
                            GPS
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3 rounded-xl font-medium bg-secondary text-white hover:bg-orange-600 transition flex items-center justify-center gap-2"
          >
            <i className="fas fa-check"></i>
            Include {selected.size} Photo{selected.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoPickerModal;
